import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../services/api';

const API_BASE_URL = getApiBaseUrl();

interface AdminActionLog {
  id: number;
  admin_id: number;
  admin_name: string;
  admin_email: string;
  target_user_id?: number;
  target_user_name?: string;
  action_type: string;
  action_details?: any;
  created_at: string;
}

const AdminActivityLog: React.FC = () => {
  const [logs, setLogs] = useState<AdminActionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('');

  const fetchLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      let url = `${API_BASE_URL}/admin/action-logs?limit=100`;
      if (filterType) {
        url += `&action_type=${filterType}`;
      }

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (err) {
      console.error('Error fetching action logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionTypeColor = (actionType: string) => {
    switch (actionType) {
      case 'approve_dispatcher':
        return 'bg-green-100 text-green-800';
      case 'reject_dispatcher':
        return 'bg-red-100 text-red-800';
      case 'create_admin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatActionType = (actionType: string) => {
    return actionType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-volexx-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 font-poppins">Admin Activity Log</h1>
        <p className="text-gray-600 mt-2 font-poppins">
          Track all admin actions for accountability and auditing
        </p>
      </div>

      <div className="mb-6 bg-white rounded-volexx shadow-sm p-4 border border-gray-200">
        <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
          Filter by Action Type
        </label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
        >
          <option value="">All Actions</option>
          <option value="approve_dispatcher">Approve Dispatcher</option>
          <option value="reject_dispatcher">Reject Dispatcher</option>
          <option value="create_admin">Create Admin</option>
        </select>
      </div>

      <div className="bg-white rounded-volexx shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 font-poppins">
            Activity History ({logs.length} {logs.length === 1 ? 'action' : 'actions'})
          </h2>
        </div>

        {logs.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500 font-poppins">No activity logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                    Target User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-poppins">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-poppins">
                      <div className="font-medium text-gray-900">{log.admin_name}</div>
                      <div className="text-sm text-gray-500">{log.admin_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-poppins">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionTypeColor(log.action_type)}`}>
                        {formatActionType(log.action_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-poppins">
                      {log.target_user_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-poppins">
                      {log.action_details ? (
                        <div className="max-w-md">
                          {log.action_details.rejection_reason && (
                            <p className="text-red-600">Reason: {log.action_details.rejection_reason}</p>
                          )}
                          {log.action_details.name && !log.action_details.rejection_reason && (
                            <p>{log.action_details.name}</p>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-volexx p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-blue-800 font-poppins">Accountability & Transparency</h3>
            <p className="text-sm text-blue-700 mt-1 font-poppins">
              All admin actions are logged for accountability. This helps maintain transparency and track who performed what actions in the system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminActivityLog;
